/**
 * Страница «Цифровой паспорт» (FE-6 каркас, FE-7/8/9 контент).
 *
 * Состав вкладки **одинаков на всех уровнях КАТО** — область, район,
 * город областного значения, сельский округ, село: во всю ширину сверху
 * «Административное деление»; ниже две колонки — слева «Население» →
 * «Численность по населённым пунктам» → «Медиамониторинг», справа
 * «Показатели СЭР» → «Индекс потребительских цен» → «Бюджет» (US-11
 * FR-1.3, раскладка DP-412, распространена на все уровни по решению
 * пользователя). Прежний областной состав (стат-карточки СЭР, адм.
 * деление из `general`, областная таблица СЭР, национальный состав)
 * с экрана снят; компоненты остались в `components/` до решения по US.
 *
 * Данные — шесть операций US-12 (задача DP-417), по одной на блок плюс
 * ИПЦ: источники обновляются с разной периодичностью, и общая операция
 * задерживала бы вкладку ради самого медленного блока. Каждый блок
 * читает свой `status` (US-12 §2): `DISABLED` и `NOT_APPLICABLE`
 * означают «блока нет вовсе» (последний — «Административное деление» на
 * городе областного значения), `NO_DATA` и `LEVEL_NOT_SUPPORTED` —
 * «показываем, но пусто»: состав вкладки одинаков на всех уровнях, и
 * необслуживаемый уровень подписывается «нет данных», а не выламывает
 * блок из раскладки. `OK` не гарантирует заполненности показателя.
 *
 * Маршрут: `/:kato/digital-passport`. KATO из URL. Видимость блока —
 * `useModulePresent`; тумблер задаётся парой (module_id, kato_level),
 * поэтому уровни выключаются независимо (US-12 §6).
 *
 * Медиамониторинг (FE-9) фетчит свой endpoint
 * `/api/public/media-monitoring`; `/api/public/general` странице больше
 * не нужен — его данные читали только снятые областные виджеты.
 */
import { Monitor } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router-dom';

import { CpiTable, type CpiRowKey } from './components/cpi/CpiTable';
import {
  DistrictBudget,
  type BudgetRowKey,
  type BudgetRowValue,
} from './components/district-budget/DistrictBudget';
import {
  DistrictDivision,
  type DivisionCounter,
} from './components/district-division/DistrictDivision';
import { DistrictSer } from './components/district-ser/DistrictSer';
import { isBlockShown, useDistrictBlock } from './components/useDistrictBlock';
import { MediaMonitoringBlock } from './components/media-monitoring/MediaMonitoring';
import { PopulationSplit } from './components/population-split/PopulationSplit';
import { SettlementPopulation } from './components/settlement-population/SettlementPopulation';
import { BlockLoader } from '../../components/common/BlockLoader';
import { WidgetErrorBoundary } from '../../components/common/WidgetErrorBoundary';
import { useKato } from '../../contexts/KatoContext';
import { useUser } from '../../contexts/UserContext';
import { MODULES } from '../../consts/modules';
import { useModulePresent } from '../../hooks/useModulePresent';
import { katoLevel } from '../../lib/kato-level';
import { stateDateLabel } from '../../lib/report-date';
import { isKatoInScope } from '../../lib/user-access';
import {
  getCpi,
  getDistrictAdminDivision,
  getDistrictBudget,
  getDistrictPopulation,
  getDistrictSer,
  getDistrictSettlements,
} from '../../requests/district.request';
import type {
  CpiResponse,
  DistrictAdminDivisionResponse,
  DivisionCounterKey,
  DistrictBudgetResponse,
  DistrictPopulationResponse,
  DistrictSerResponse,
  DistrictSettlementsResponse,
} from '../../types/district-passport';

export const DigitalPassportPage = () => {
  const { t, i18n } = useTranslation();
  const { kato: katoParam } = useParams<{ kato: string }>();
  const { selectedKato, setSelectedKato } = useKato();
  const { user } = useUser();
  const showMedia = useModulePresent(MODULES.digitalPassport.mediaMonitoring);
  const showSerTable = useModulePresent(MODULES.digitalPassport.serTable);
  // Блоки вкладки (US-11). Идентификаторы заведены в каталоге видимости
  // (DP-467), тумблеры — на пару (module_id, kato_level), поэтому один и
  // тот же блок выключается на каждом уровне отдельно (US-12 §6).
  const showDistrictDivision = useModulePresent(MODULES.digitalPassport.districtDivision);
  const showDistrictPopulation = useModulePresent(
    MODULES.digitalPassport.districtPopulation,
  );
  const showSettlements = useModulePresent(MODULES.digitalPassport.settlements);
  const showCpi = useModulePresent(MODULES.digitalPassport.cpi);
  const showBudget = useModulePresent(MODULES.digitalPassport.districtBudget);

  const katoNum = katoParam ? Number.parseInt(katoParam, 10) : NaN;
  const katoValid = Number.isFinite(katoNum);
  // КАТО приходит из URL, а он переживает и смену учётки, и закладку. Чужой
  // КАТО дал бы 403 по каждому виджету — страница выглядела бы «пустой», а не
  // «недоступной».
  const katoAllowed = katoValid && isKatoInScope(katoNum, user?.kato_access);

  // Sync URL kato → KatoContext so ContextSidebar and other consumers stay in sync.
  useEffect(() => {
    if (katoAllowed) setSelectedKato(katoNum);
  }, [katoNum, katoAllowed, setSelectedKato]);


  // Состав вкладки одинаков на всех уровнях КАТО, поэтому блоки
  // запрашиваются везде: уровень, который операция не обслуживает, она
  // объявляет сама (`LEVEL_NOT_SUPPORTED` → блок с «нет данных»).
  const districtKato = katoAllowed ? katoNum : null;

  // Шесть операций US-12 — каждая своим запросом (FR-1.1). Выключенный
  // супер-админом блок не запрашивается: лишний 200 со `status: DISABLED`
  // экрану ничего не добавляет.
  const division = useDistrictBlock<DistrictAdminDivisionResponse>(
    getDistrictAdminDivision,
    districtKato,
    showDistrictDivision,
  );
  const population = useDistrictBlock<DistrictPopulationResponse>(
    getDistrictPopulation,
    districtKato,
    showDistrictPopulation,
  );
  /**
   * Город областного значения: подчинённых населённых пунктов у него нет,
   * поэтому «Численность населения по населённым пунктам» на нём не
   * выводится. Признак города по коду КАТО не виден (город и район —
   * один уровень) и приходит единственным способом — статусом
   * `NOT_APPLICABLE` у «Административного деления» (US-12 FR-5.3).
   * Выключенное супер-админом деление не запрашивается вовсе — признака
   * тогда нет и разрез остаётся на экране: это уже задача выдачи
   * (отдать `NOT_APPLICABLE` и по `settlement-population`).
   */
  const isTown = division.data?.status === 'NOT_APPLICABLE';

  const settlements = useDistrictBlock<DistrictSettlementsResponse>(
    getDistrictSettlements,
    districtKato,
    showSettlements && !isTown,
  );
  const districtSer = useDistrictBlock<DistrictSerResponse>(
    getDistrictSer,
    districtKato,
    showSerTable,
  );
  const budget = useDistrictBlock<DistrictBudgetResponse>(
    getDistrictBudget,
    districtKato,
    showBudget,
  );
  const cpi = useDistrictBlock<CpiResponse>(getCpi, districtKato, showCpi);

  if (!katoValid) {
    return <Navigate to="/" replace />;
  }

  // Не своя территория — уводим на доступную, а не показываем пустые виджеты.
  if (!katoAllowed && selectedKato !== null && selectedKato !== katoNum) {
    return <Navigate to={`/${selectedKato}/digital-passport`} replace />;
  }

  const isKz = i18n.language === 'kz';

  /**
   * Подпись отчётного периода блока — готовой строкой из выдачи (US-12
   * FR-2): экран её не собирает из календаря. `is_current = false` —
   * данных за текущий период нет и отдан последний доступный; это
   * подписывается явно, а не выглядит как «нет данных» (FR-2.3).
   */
  const periodCaption = (
    period: { label_ru: string | null; label_kz: string | null; is_current: boolean } | null,
  ): string | null => {
    const label = (isKz ? period?.label_kz : period?.label_ru) ?? null;
    if (label === null) return null;
    return period?.is_current === false
      ? `${label} · ${t('digitalPassport.lastAvailablePeriod')}`
      : label;
  };

  // Счётчики административного деления: значения как есть, без пересчёта
  // (US-11 FR-2.2). Отчётная дата — дата состояния справочника.
  const divisionCounts =
    division.data === null
      ? null
      : {
          okrugs: division.data.rural_district_count,
          villages: division.data.village_count,
          cities: division.data.city_count,
          districts: division.data.district_count,
          oblastCities: division.data.oblast_city_count,
        };

  // Состав блока объявляет выдача (US-41): ключ ответа → ключ плитки.
  const DIVISION_KEY_MAP: Record<DivisionCounterKey, DivisionCounter> = {
    districts: 'districts',
    oblast_cities: 'oblastCities',
    cities: 'cities',
    okrugs: 'okrugs',
    villages: 'villages',
  };
  const divisionLayout = (division.data?.counters ?? []).map(
    (key) => DIVISION_KEY_MAP[key],
  );

  // Населённые пункты: в items только НП (округа и строка района
  // отфильтрованы бэкендом), признак города — из выдачи (FR-4.2).
  const settlementItems = (settlements.data?.items ?? []).flatMap((item) =>
    item.kato === null
      ? []
      : [
          {
            kato: item.kato,
            name_ru: item.name_ru ?? '',
            name_kz: item.name_kz ?? '',
            population: item.population,
            isCity: item.is_town,
          },
        ],
  );

  /** Ключ строки бюджета в выдаче → ключ строки виджета (состав фиксирован). */
  const BUDGET_KEY_MAP: Record<string, BudgetRowKey> = {
    total: 'total',
    own_revenue: 'ownIncome',
    rb_transfers_and_credits: 'republicTransfers',
    transfers: 'transfers',
    subventions: 'subventions',
    credits: 'credits',
  };

  // Две метрики исполнения не сливаются в одну колонку (US-12 FR-9.2).
  const budgetValues = Object.fromEntries(
    (budget.data?.rows ?? []).flatMap((row) => {
      const key = row.key !== null ? BUDGET_KEY_MAP[row.key] : undefined;
      if (key === undefined) return [];
      return [
        [
          key,
          {
            plan: row.plan_yearly,
            fact: row.fact_period,
            annualPct: row.annual_plan_utilization_pct,
            periodPct: row.period_plan_execution_pct,
          } satisfies BudgetRowValue,
        ],
      ];
    }),
  ) as Partial<Record<BudgetRowKey, BudgetRowValue>>;

  /** Ключ категории ИПЦ в выдаче → ключ строки виджета. */
  const CPI_KEY_MAP: Record<string, CpiRowKey> = {
    total: 'total',
    food: 'food',
    nonfood: 'nonFood',
    services: 'services',
  };

  const cpiValues = Object.fromEntries(
    (cpi.data?.categories ?? []).flatMap((row) => {
      const key = row.key !== null ? CPI_KEY_MAP[row.key] : undefined;
      return key === undefined ? [] : [[key, row.value]];
    }),
  ) as Partial<Record<CpiRowKey, number | null>>;

  return (
    <article
      className="space-y-3.5 p-5"
      data-testid="digital-passport-page"
      data-kato={katoNum}
    >
      <div className="flex items-center gap-4 py-3">
        <h1 className="shrink-0 text-xl font-extrabold">
          {t('digitalPassport.title')}
        </h1>
        <div className="h-px flex-1 bg-surface-border" aria-hidden="true" />
        <Monitor
          className="size-6 shrink-0 text-brand-600"
          strokeWidth={1.8}
          aria-hidden="true"
        />
      </div>

      {/* Раскладка одинакова на всех уровнях КАТО (US-11 FR-1.3): во всю
          ширину сверху — административное деление; ниже две колонки.
          Слева Население → Численность по НП → Медиамониторинг, справа
          Показатели СЭР → ИПЦ → Бюджет. Выключенный блок места не
          занимает — остальные в своей колонке смыкаются.

          Для города областного значения «Административное деление» не
          выводится вовсе: выдача отвечает `NOT_APPLICABLE` (US-12
          FR-5.3) — признак города приходит из справочника, по коду КАТО
          он не виден. */}
      {showDistrictDivision &&
        isBlockShown(division.data?.status ?? null) && (
          <WidgetErrorBoundary key={`district-division-${katoNum}`}>
            {division.loading ? (
              <BlockLoader />
            ) : (
              <DistrictDivision
                counts={divisionCounts}
                layout={divisionLayout}
                oblast={katoLevel(katoNum) === 'OBLAST'}
                // Дата состояния справочника, а не период накопления.
                periodLabel={stateDateLabel(
                  division.data?.as_of_date ??
                    division.data?.valid_from ??
                    null,
                  isKz,
                )}
              />
            )}
          </WidgetErrorBoundary>
        )}

      {/* items-start: в каждой колонке по три блока, и без него
          колонка растягивалась бы до высоты ряда, а `h-full` внутри
          раздувал каждую карточку на всю эту высоту — пустой блок
          занимал экран. Карточки районной вкладки по высоте не
          выравниваются: они разного размера по составу. */}
      <div className="grid grid-cols-1 items-start gap-3.5 xl:grid-cols-2">
        <div className="space-y-3.5">
          {showDistrictPopulation &&
            isBlockShown(population.data?.status ?? null) && (
              <WidgetErrorBoundary key={`district-population-${katoNum}`}>
                {population.loading ? (
                  <BlockLoader />
                ) : (
                  // Итог и разбивка город/село — из одного источника по
                  // методике БНС (US-12 FR-6.2): разбивку «Мониторинга
                  // СНП» подставлять нельзя, у неё другое определение
                  // городского населения.
                  <PopulationSplit
                    total={population.data?.total ?? null}
                    urban={population.data?.urban ?? null}
                    rural={population.data?.rural ?? null}
                    periodLabel={periodCaption(
                      population.data?.period ?? null,
                    )}
                  />
                )}
              </WidgetErrorBoundary>
            )}
          {showSettlements &&
            !isTown &&
            isBlockShown(settlements.data?.status ?? null) && (
              <WidgetErrorBoundary key={`settlements-${katoNum}`}>
                {settlements.loading ? (
                  <BlockLoader />
                ) : (
                  // Итог — официальный из выдачи, а не сумма строк
                  // (US-12 FR-7.3). Разрез годовой, и отставание года
                  // подписано периодом, а не пустым блоком (FR-7.5).
                  <SettlementPopulation
                    items={settlementItems}
                    total={settlements.data?.total ?? null}
                    periodLabel={periodCaption(
                      settlements.data?.period ?? null,
                    )}
                  />
                )}
              </WidgetErrorBoundary>
            )}
          {showMedia && (
            <WidgetErrorBoundary key={`media-${katoNum}`}>
              {/* Блок сохраняется на всех уровнях (FR-6А, решение 6):
                  тот же виджет, меняется только КАТО запроса. */}
              <MediaMonitoringBlock kato={katoNum} />
            </WidgetErrorBoundary>
          )}
        </div>
        <div className="space-y-3.5">
          {showSerTable &&
            isBlockShown(districtSer.data?.status ?? null) && (
              <WidgetErrorBoundary key={`district-ser-${katoNum}`}>
                {/* Районный набор отраслей и переход на страницу
                    отрасли в «Аналитике» (US-11 FR-5.1, FR-5.6) —
                    отдельный блок, а не режим областной таблицы. */}
                <DistrictSer
                  data={districtSer.data}
                  loading={districtSer.loading}
                />
              </WidgetErrorBoundary>
            )}
          {showCpi && isBlockShown(cpi.data?.status ?? null) && (
            <WidgetErrorBoundary key={`cpi-${katoNum}`}>
              {cpi.loading ? (
                <BlockLoader />
              ) : (
                // Районного разреза у ИПЦ нет: уровень берётся из
                // выдачи (`territory_level`), а не из уровня КАТО
                // запроса — иначе областная цифра будет прочитана
                // как районная (US-12 FR-11.3).
                <CpiTable
                  values={cpiValues}
                  level={
                    cpi.data?.territory_level === 'DISTRICT'
                      ? 'district'
                      : 'oblast'
                  }
                  periodLabel={periodCaption(cpi.data?.period ?? null)}
                />
              )}
            </WidgetErrorBoundary>
          )}
          {showBudget && isBlockShown(budget.data?.status ?? null) && (
            <WidgetErrorBoundary key={`district-budget-${katoNum}`}>
              {budget.loading ? (
                <BlockLoader />
              ) : (
                // Суммы — в единице выдачи (тыс. ₸ казначейской
                // формы): пересчёт на экране это та самая ошибка в
                // 1000 раз (US-12 FR-3.1).
                <DistrictBudget
                  values={budgetValues}
                  unitLabel={
                    (isKz
                      ? budget.data?.unit_kz
                      : budget.data?.unit_ru) ?? null
                  }
                  calculationRule={
                    (isKz
                      ? budget.data?.calculation_rule_kz
                      : budget.data?.calculation_rule_ru) ?? null
                  }
                  periodLabel={periodCaption(budget.data?.period ?? null)}
                  oblast={katoLevel(katoNum) === 'OBLAST'}
                />
              )}
            </WidgetErrorBoundary>
          )}
        </div>
      </div>
    </article>
  );
};
